import type { ReactNode } from "react"; // Use type import
import React, { createContext, useCallback, useEffect, useRef,useState } from "react"; // Added useCallback, useRef

import type { Filter } from "./filter-accordion/types"; // Use type import

export interface FilterData {
  id: string | number;
  timeAdded: number;
  name: string;
  parentId?: number;
  // Language-neutral i18n key for options whose label is a translated string
  // (e.g. Shift Status, Shift Type). When present, display surfaces translate
  // via this key instead of the frozen `name` captured at selection time.
  labelKey?: string;
  // An option that is present but cannot be picked here. It stays in the list
  // rather than disappearing, so a filter carried across tabs still shows the
  // value the page holds; `disabledReasonKey` is the i18n key for the tooltip
  // that says why it is greyed out.
  disabled?: boolean;
  disabledReasonKey?: string;
}

export interface Data {
  id: string;
  // any key value pair
  [key: string]: any;
}

export interface DataRelation {
  to: string; // key of filter, applied filter on this will reduce data of from
  from: string; // key of filter, get reduced data based on applied filter of to
  mappings: { fromId: number; toIds: number[] }[];
}

export interface AppliedFilter {
  type: string;
  key?: string;
  timeAdded: number;
  data: FilterData[];
}

interface FilterContextType {
  appliedFilters: AppliedFilter[];
  localAppliedFilters: string | null; // Keep for reference if needed elsewhere
  setAppliedFilters: React.Dispatch<React.SetStateAction<AppliedFilter[]>>;
  filterAccordionHeight: number;
  setFilterAccordionHeight: React.Dispatch<React.SetStateAction<number>>;
  setSearchedValue: React.Dispatch<React.SetStateAction<string>>;
  searchedValue: string;
  open: boolean;
  setFilterOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: Data[];
  setData: React.Dispatch<React.SetStateAction<Data[]>>;
  dataRelations: DataRelation[];
  setDataRelations: React.Dispatch<React.SetStateAction<DataRelation[]>>;
  filters: Filter[];
  // Changed signature to accept new filters and trigger cascade
  setFilters: (newFilters: Filter[]) => void;
  isLoading?: boolean;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  filterKey: string;
}

export const FilterContext = createContext<FilterContextType | undefined>(
  undefined
);

interface FilterProviderProps {
  children: ReactNode;
  filterKey: string;
}

// Helper: Custom hook for deep comparison to avoid unnecessary effect runs
function useDeepCompareMemoize<T>(value: T) {
  const ref = useRef<T>();
  // it can be done by using useMemo as well
  // but useRef is rather cleaner and simpler
  if (!deepEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}
// Basic deep equal function (replace with lodash.isEqual or similar for production)
function deepEqual(obj1: any, obj2: any): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}


// Extracted cascade logic into a reusable function
const runCascadeLogic = (
  existingFilters: Filter[],
  currentAppliedFilters: AppliedFilter[],
  currentDataRelations: DataRelation[]
): Filter[] => {
  // Ensure inputs are valid arrays
  if (!Array.isArray(existingFilters) || !Array.isArray(currentAppliedFilters) || !Array.isArray(currentDataRelations)) {
    console.warn("Cascade logic received invalid input types.");
    return existingFilters;
  }

  // Deep clone to avoid mutating the original state directly
  const updatedFilters = JSON.parse(JSON.stringify(existingFilters)) as Filter[];
  let changed = true;
  let iterations = 0; // Prevent potential infinite loops in cascade logic itself
  const MAX_ITERATIONS = 10; // Safety break

  // Track which target filter keys have been actively constrained by a relation with actual selection/reduction
  const constrainedTargets = new Set<string>();

  while (changed && iterations < MAX_ITERATIONS) {
    changed = false;
    iterations++;

    for (const relation of currentDataRelations) {
      const fromFilter = updatedFilters.find(f => f.key === relation.from);
      const toFilterIndex = updatedFilters.findIndex(f => f.key === relation.to);

      // Ensure both filters exist in the current list
      if (!fromFilter || toFilterIndex === -1) continue;

      // 1️⃣ Find the applied filter for the 'from' key
      const selectedAppliedFilter = currentAppliedFilters.find(f => f.key === relation.from);
      let fromIds: (string | number)[] = [];

      if (selectedAppliedFilter && Array.isArray(selectedAppliedFilter.data)) {
        // Use IDs from the currently applied filter first
        fromIds = selectedAppliedFilter.data.map(item => item.id);
      } else {
        // No applied filter for 'from' key — check if 'from' was reduced by another relation
        const fromFilterOriginalData = Array.isArray(fromFilter.data) ? fromFilter.data : [];
        const fromFilterReducedData = Array.isArray(fromFilter.reducedData) ? fromFilter.reducedData : [];
        const isFromReduced = fromFilterReducedData.length < fromFilterOriginalData.length;

        if (isFromReduced && fromFilterReducedData.length > 0) {
          // 2️⃣ Fallback: 'from' was reduced by a cascading relation, use its reducedData
          // This allows cascading effects (A affects B, B affects C)
          fromIds = fromFilterReducedData.map(item => item.id);
        } else if (!isFromReduced) {
          // 'from' is not reduced (no selection, no cascade) — only reset 'to' if it hasn't
          // already been constrained by another relation (e.g., work_center → shift_pattern)
          if (!constrainedTargets.has(relation.to)) {
            const targetFilter = updatedFilters[toFilterIndex];
            const targetData = Array.isArray(targetFilter.data) ? targetFilter.data : [];
            const prevReducedData = Array.isArray(targetFilter.reducedData) ? targetFilter.reducedData : [];
            if (prevReducedData.length !== targetData.length ||
                !targetData.every(item => prevReducedData.some(p => p.id === item.id))) {
              updatedFilters[toFilterIndex] = {
                ...targetFilter,
                reducedData: targetData,
                searchedData: targetData,
              };
              changed = true;
            }
          }
          continue;
        }
      }

      // If no 'from' IDs found, we can't reduce the 'to' filter based on this relation
      if (fromIds.length === 0 && !selectedAppliedFilter) {
        continue;
      }


      const targetFilter = updatedFilters[toFilterIndex];
      // Ensure targetFilter.data is an array
      const targetData = Array.isArray(targetFilter.data) ? targetFilter.data : [];

      let newReducedData: Filter["reducedData"] = []; // Default to empty array

      if (fromIds.length > 0) {
        // Normalize fromIds to numbers for comparison with mapping.fromId (which is number)
        const normalizedFromIds = fromIds.map(id => typeof id === "string" ? parseInt(id, 10) : id);
        // Find mappings relevant to the current fromIds
        const relevantMappings = Array.isArray(relation.mappings)
          ? relation.mappings.filter(mapping => normalizedFromIds.includes(mapping.fromId))
          : [];
        // Get all possible 'to' IDs based on the relevant mappings
        const allowedToIds = relevantMappings.flatMap(mapping => Array.isArray(mapping.toIds) ? mapping.toIds : []);

        // Filter the target filter's original data based on allowed 'to' IDs
        // Also keep child items whose parent is in the allowed set (for hierarchical filters)
        // Use loose comparison to handle string/number ID mismatches
        newReducedData = targetData.filter(item => {
          const itemId = typeof item.id === "string" ? parseInt(item.id, 10) : item.id;
          const parentId = item.parentId != null ? (typeof item.parentId === "string" ? parseInt(item.parentId, 10) : item.parentId) : undefined;
          return allowedToIds.includes(itemId) ||
            (parentId != null && allowedToIds.includes(parentId));
        });

      } else {
        // If fromIds is empty (because an applied filter selected nothing matching mappings),
        // the resulting reduced data for 'to' should also be empty.
        newReducedData = [];
      }


      // Ensure targetFilter.reducedData is an array for comparison
      const prevReducedData = Array.isArray(targetFilter.reducedData) ? targetFilter.reducedData : [];
      const prevIds = prevReducedData.map(item => item.id);
      const newIds = newReducedData.map(item => item.id);

      // Check if the reduced data has actually changed
      const hasDifference =
        prevIds.length !== newIds.length ||
        !prevIds.every(id => newIds.includes(id)) || // More robust check
        !newIds.every(id => prevIds.includes(id));

      // Mark this target as constrained by an active relation
      constrainedTargets.add(relation.to);

      if (hasDifference) {
        // Update the target filter with new reduced data and reset searched data
        updatedFilters[toFilterIndex] = {
          ...targetFilter,
          reducedData: newReducedData,
          searchedData: newReducedData, // Reset search when reduced data changes
        };
        changed = true; // Mark that a change occurred to potentially loop again
      }
    }
  }
  if (iterations === MAX_ITERATIONS) {
    console.warn("Filter cascade reached maximum iterations. Check for circular dependencies in dataRelations.");
  }

  // Return the processed list
  return updatedFilters;
};


export const FilterProvider: React.FC<FilterProviderProps> = ({
                                                                children,
                                                                filterKey,
                                                              }) => {
  // --- State Initialization ---
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>(() => {
    try {
      const localData = localStorage.getItem(`appliedFilters-${filterKey}`);
      return localData ? JSON.parse(localData) : [];
    } catch (e) {
      console.error("Failed to parse appliedFilters from localStorage:", e);
      return [];
    }
  });
  const [filterAccordionHeight, setFilterAccordionHeight] = useState<number>(480);
  const [searchedValue, setSearchedValue] = useState("");
  const [open, setFilterOpen] = useState(false);
  const [internalFilters, setInternalFilters] = useState<Filter[]>([]); // Renamed state
  const [data, setData] = useState<Data[]>([]);
  const [dataRelations, setDataRelations] = useState<DataRelation[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Use refs to hold the latest values for stable callbacks
  const appliedFiltersRef = useRef(appliedFilters);
  const dataRelationsRef = useRef(dataRelations);
  const internalFiltersRef = useRef(internalFilters);

  // Update refs whenever the state changes
  useEffect(() => { appliedFiltersRef.current = appliedFilters; }, [appliedFilters]);
  useEffect(() => { dataRelationsRef.current = dataRelations; }, [dataRelations]);
  useEffect(() => { internalFiltersRef.current = internalFilters; }, [internalFilters]);


  // --- Effects ---

  // Effect to update localStorage when appliedFilters change
  useEffect(() => {
    try {
      localStorage.setItem(
        `appliedFilters-${filterKey}`,
        JSON.stringify(appliedFilters)
      );
    } catch (e) {
      console.error("Failed to save appliedFilters to localStorage:", e);
    }
  }, [appliedFilters, filterKey]);

  // Effect to handle initialization and filterKey changes
  const isMounted = useRef(false);
  useEffect(() => {
    // On the first pass there is nothing to do: the useState initializer above
    // already read this same key. Re-reading it here would overwrite whatever a
    // consumer seeded from its own effect — children's effects run before the
    // provider's, so a page that applies filters on mount would have them
    // thrown away one commit later.
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    // The key changed, so the stored selection belongs to a different page or
    // user — load that one instead.
    let initialAppliedFilters: AppliedFilter[] = [];
    try {
      const localData = localStorage.getItem(`appliedFilters-${filterKey}`);
      initialAppliedFilters = localData ? JSON.parse(localData) : [];
    } catch (e) {
      initialAppliedFilters = [];
    }

    setAppliedFilters(initialAppliedFilters);
  }, [filterKey]); // Runs only when filterKey changes

  // Memoize dataRelations to stabilize dependencies
  const memoizedDataRelations = useDeepCompareMemoize(dataRelations);

  // Effect to run cascade logic when appliedFilters or dataRelations change
  useEffect(() => {
    // Run cascade on the CURRENT internal filters using the NEW appliedFilters/dataRelations
    const cascadedFilters = runCascadeLogic(
      internalFiltersRef.current, // Use ref for current filters
      appliedFilters,
      memoizedDataRelations || [] // Use memoized relations
    );

    // Only update state if the cascaded result is different
    if (!deepEqual(internalFiltersRef.current, cascadedFilters)) {
      internalFiltersRef.current = cascadedFilters;
      setInternalFilters(cascadedFilters);
    }

    // Prune applied filters: remove items that no longer exist in reduced/available data
    if (appliedFilters.length > 0 && cascadedFilters.length > 0) {
      const validIdsByKey = new Map<string, Set<string | number>>();
      cascadedFilters.forEach((f) => {
        const items = Array.isArray(f.reducedData) ? f.reducedData : f.data;
        if (Array.isArray(items)) {
          const idSet = new Set<string | number>(items.map((item: FilterData) => item.id));
          if (f.key) validIdsByKey.set(f.key, idSet);
        }
      });

      const prunedFilters = appliedFilters
        .map((af) => {
          const validIds = af.key ? validIdsByKey.get(af.key) : undefined;
          if (!validIds) return af; // no matching filter definition, keep as-is
          const validData = af.data.filter((item) => validIds.has(item.id));
          if (validData.length === af.data.length) return af; // no change
          if (validData.length === 0) return null; // remove entirely
          return { ...af, data: validData };
        })
        .filter(Boolean) as AppliedFilter[];

      if (!deepEqual(appliedFilters, prunedFilters)) {
        setAppliedFilters(prunedFilters);
        appliedFiltersRef.current = prunedFilters;
      }
    }
    // Use memoized relations and appliedFilters as dependencies
  }, [appliedFilters, memoizedDataRelations]);


  // --- Context Value ---

  // Stable function to set initial/new filters AND run cascade immediately
  const stableSetFilters = useCallback((newFilters: Filter[]) => {
    // Validate saved applied filters against the new filter data
    // Remove any applied filter items that no longer exist in the current data
    const currentApplied = appliedFiltersRef.current;
    if (currentApplied.length > 0) {
      // Build a set of all valid item IDs from the new filters
      const validIdsByType = new Map<string, Set<string | number>>();
      newFilters.forEach((f) => {
        const items = Array.isArray(f.reducedData) ? f.reducedData : f.data;
        const idSet = new Set<string | number>(items.map((item: FilterData) => item.id));
        validIdsByType.set(f.name, idSet);
      });

      const validatedFilters = currentApplied
        .map((af) => {
          const validIds: Set<string | number> | undefined = validIdsByType.get(af.type);
          if (!validIds) return null; // filter type no longer exists
          const validData = af.data.filter((item) => validIds.has(item.id));
          if (validData.length === 0) return null;
          return { ...af, data: validData };
        })
        .filter(Boolean) as AppliedFilter[];

      if (!deepEqual(currentApplied, validatedFilters)) {
        setAppliedFilters(validatedFilters);
        appliedFiltersRef.current = validatedFilters;
      }
    }

    // Run cascade logic immediately with the new filters and CURRENT appliedFilters/dataRelations
    const cascadedFilters = runCascadeLogic(
      newFilters,
      appliedFiltersRef.current, // Use ref for current applied filters
      dataRelationsRef.current // Use ref for current relations
    );

    // Update the internal state
    // Check if different before setting to prevent potential loops if called rapidly
    if (!deepEqual(internalFiltersRef.current, cascadedFilters)) {
      // Update ref synchronously so cascade effect doesn't overwrite with stale data
      internalFiltersRef.current = cascadedFilters;
      setInternalFilters(cascadedFilters);
    }
  }, []); // No dependencies, relies on refs


  return (
    <FilterContext.Provider
      value={{
        appliedFilters,
        localAppliedFilters: null, // Value from useState initializer, maybe remove if not needed
        setAppliedFilters, // Pass down the state setter directly
        filterAccordionHeight,
        setFilterAccordionHeight,
        searchedValue,
        setSearchedValue,
        open,
        setFilterOpen,
        data,
        setData,
        dataRelations,
        setDataRelations, // Pass down the state setter directly
        filters: internalFilters, // Expose the processed internal state
        setFilters: stableSetFilters, // Expose the stable function
        isLoading,
        setIsLoading,
        filterKey,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};